import React, { useEffect, useState } from "react";

import { motion } from "framer-motion";
import { AudioLines, Dot } from "lucide-react";
import { useAnimate } from "motion/react";
import { useNavigate, useParams } from "react-router";

import MathingAss from "~/ass/mathing.json";
import RunningDogAss from "~/ass/running_dog.json";
import SleepyDogAss from "~/ass/sleepy_dog.json";
import WavingDogAss from "~/ass/waving_dog.json";
import AnimatedSticker from "~/components/animated-stickers";
import Typography from "~/components/typography";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from "~/components/ui/card";
import {
  Carousel,
  type CarouselApi,
  CarouselContent,
  CarouselItem,
} from "~/components/ui/carousel";
import {
  useGenAudio,
  useGenQuiz,
  useGetFlashcard,
} from "~/lib/hooks/api.hooks";
import { cn } from "~/lib/utils";

const ReadAudioButton: React.FC<{ flashcardId: string; word: string }> = ({
  flashcardId,
  word,
}) => {
  const { mutateAsync, isPending } = useGenAudio();
  const handlePlay = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const blob = await mutateAsync({ flashcardId, word });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.play();
    } catch {
      // Error is handled via hook notifications
    }
  };
  return (
    <Button
      variant="ghost"
      onClick={handlePlay}
      className="py-8"
      disabled={isPending}
    >
      <AudioLines className="text-muted-foreground !w-11 !h-11" size={40} />
    </Button>
  );
};

const Flashcard: React.FC<{
  content: {
    note: string;
    word: string;
    translation: string;
  };
  flashcardId: string;
}> = (props) => {
  const { note, word, translation } = props.content;
  const { flashcardId } = props;
  const [flip, setFlip] = useState(true);
  const handleClick = () => setFlip((prev) => !prev);

  return (
    <CarouselItem>
      <div
        className="flex items-center justify-center p-5"
        style={{ perspective: "1000px" }}
      >
        <motion.div
          style={{
            width: "20rem",
            minHeight: "20rem",
            position: "relative",
            transformStyle: "preserve-3d",
          }}
          animate={{ rotateY: flip ? 0 : 180 }}
          transition={{ duration: 0.7 }}
        >
          <Dot
            className="text-lime-600 absolute left-1/2 top-0 -translate-1/2 z-10"
            size={50}
          />
          <div
            className="front"
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              backfaceVisibility: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <Card
              onClick={handleClick}
              className="w-full h-full text-center flex flex-col items-center justify-between"
            >
              <CardHeader>
                <Typography variant="h1">Word</Typography>
              </CardHeader>
              <CardContent>
                <Typography variant="p">{word}</Typography>
              </CardContent>
              <CardFooter>
                <ReadAudioButton flashcardId={flashcardId} word={word} />
              </CardFooter>
            </Card>
          </div>
          <div
            className="back"
            style={{
              position: "absolute",
              width: "100%",
              height: "100%",
              backfaceVisibility: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transform: "rotateY(180deg)",
              cursor: "pointer",
            }}
          >
            <Card
              onClick={handleClick}
              className="w-full h-full text-center flex flex-col items-center justify-between"
            >
              <CardHeader>
                <Typography variant="h1">Translation</Typography>
              </CardHeader>
              <CardContent>
                <Typography variant="p">{translation}</Typography>
              </CardContent>
              <CardFooter className="flex flex-col">
                <Typography variant="muted">{note}</Typography>
              </CardFooter>
            </Card>
          </div>
        </motion.div>
      </div>
    </CarouselItem>
  );
};

const Flashcards = () => {
  // REQUIRED AUTHENTICATION
  // useSession({ required: true });

  // PARAMS
  const { flashcards_id } = useParams<{ flashcards_id: string }>();

  // API HOOKS
  const flashcards = useGetFlashcard(flashcards_id as string);
  const generateQuiz = useGenQuiz();

  // STATE HOOKS
  const [api, setApi] = useState<CarouselApi>();
  const [current, setCurrent] = useState(0);
  const [canScrollNext, setCanScrollNext] = useState(false); // Initial false is safer
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [scope, animate] = useAnimate();

  // OTHERS
  const navigate = useNavigate();

  const isLastCard = current === (flashcards.data?.items.length || 0);

  useEffect(() => {
    if (!flashcards.isSuccess) {
      return;
    }

    const newLeft = `${(current * 100) / (flashcards.data?.items.length || 1)}%`;

    animate(
      scope.current,
      { left: newLeft },
      {
        type: "tween",
        duration: 1,

        onComplete: () => {
          setIsMoving(false);
        },
        onPlay: () => {
          setIsMoving(true);
        },
      },
    );
  }, [current]);

  useEffect(() => {
    if (!api) {
      return;
    }

    const updateStates = () => {
      setCurrent(api.selectedScrollSnap() + 1);
      setCanScrollNext(api.canScrollNext());
      setCanScrollPrev(api.canScrollPrev());
    };

    // Set initial states
    updateStates();

    // Update states on selection change
    api.on("select", updateStates);

    // Cleanup event listener
    return () => {
      api.off("select", updateStates);
    };
  }, [api, flashcards.isSuccess, flashcards.data?.items.length]);

  async function handleGoToQuiz() {
    const generatedQuiz = flashcards.data?.phrase
      ? await generateQuiz.mutateAsync({ phrase: flashcards.data.phrase })
      : undefined;

    navigate(`/app/quiz/${generatedQuiz?.id}`);
  }

  if (flashcards.isPending) {
    return (
      <AnimatedSticker
        title="Fetching"
        desc="Hold on a second, loading the data"
        data={{
          raw: RunningDogAss,
        }}
      />
    );
  }

  if (generateQuiz?.status === "pending") {
    return (
      <AnimatedSticker
        title="Gnerating a quiz..."
        desc="Hang tight! We're creating your quiz..."
        data={{
          raw: MathingAss,
        }}
      />
    );
  }

  return (
    <div className="flex px-4 flex-col justify-center max-w-2xl gap-4 w-full my-auto isolate pt-4">
      <Carousel setApi={setApi} className="w-full relative max-w-2xl">
        <CarouselContent>
          {flashcards.isSuccess &&
            flashcards.data?.items.map((i, idx) => (
              <Flashcard
                key={idx}
                content={i}
                flashcardId={flashcards_id as string}
              />
            ))}
        </CarouselContent>
      </Carousel>
      <div className="flex flex-col gap-4 text-center mb-32 ">
        <Typography className="text-center" variant="muted">
          Click to flip
        </Typography>
        <div className="flex flex-wrap justify-between">
          <Button
            variant="secondary"
            disabled={!canScrollPrev}
            onClick={() => api?.scrollPrev()}
            className="z-100 basis-0.5"
          >
            Prev
          </Button>
          <Button
            disabled={!canScrollNext}
            onClick={() => api?.scrollNext()}
            className="z-100 basis-0.5"
          >
            Next
          </Button>
        </div>
        <Button
          variant={isLastCard ? "default" : "ghost"}
          onClick={handleGoToQuiz}
          className={cn("z-100 w-full", !isLastCard && "underline")}
        >
          {isLastCard ? "Go" : "Skip"} to Quiz
        </Button>
      </div>
      <div
        className="absolute bottom-4"
        style={{ left: "0%", transform: "translateX(-74%)" }}
        ref={scope}
      >
        {(isMoving || !isLastCard) && (
          <AnimatedSticker responsive data={{ raw: SleepyDogAss }} />
        )}
        {!isMoving && isLastCard && (
          <AnimatedSticker responsive flipH data={{ raw: WavingDogAss }} />
        )}
      </div>
    </div>
  );
};

export default Flashcards;
